import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { Control, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Employment, EmploymentOption } from "../../../models/Consultant";
import AppDatePicker from "../../Core/AppDatePicker/AppDatePicker";
import AppInputNew from "../../Core/AppInput/AppInputNew";
import AppText from "../../Core/AppText/AppText";
import Checkbox from "../../Core/Checkbox/Checkbox";
import CollectiveAgreementDropdown from "../CollectiveAgreementDropdown/CollectiveAgreementDropdown";

interface EmploymentAndSalaryProps {
	value?: Employment;
	onChange?: (employment: Employment) => void;
	errors?: any;
	isCreate?: boolean;
}

const EmploymentAndSalary: React.FC<EmploymentAndSalaryProps> = ({
	onChange,
	errors,
	value,
	isCreate,
}) => {
	const [colAgreementId, setColAgreementId] = useState<string | undefined>(undefined);
	const [union, setUnion] = useState("");
	const [title, setTitle] = useState("");
	const [employmentFirstStartDate, setEmploymentFirstStartDate] = useState<Date | undefined>();
	const [employmentStart, setEmploymentStart] = useState<Date | undefined>();
	const [employmentEndDate, setEmploymentEndDate] = useState<Date | undefined>(undefined);

	const [employmentOption, setEmploymentOption] = useState<EmploymentOption>();

	const { t } = useTranslation();

	useEffect(() => {
		if (value) {
			const newColAgreementId = value.colAgreement?.id;
			setColAgreementId(newColAgreementId);
			setUnion(value.union || "");
			setTitle(value.title || "");

			setEmploymentFirstStartDate(dayjs(value.firstStarted).toDate());
			setEmploymentStart(dayjs(value.started || value.firstStarted).toDate());
			setEmploymentEndDate(value.ended ? dayjs(value.ended).toDate() : undefined);
		} else {
			// setEmploymentStart(dayjs().toDate());
			// setEmploymentFirstStartDate(dayjs().toDate());
		}

		setEmploymentOption(value?.employmentOption);
	}, [value]);

	const handleColAgreement = (agreementId: string) => {
		setColAgreementId(agreementId);
		triggerOnChange({ colAgreement: agreementId ? { id: agreementId } : undefined });
	};

	const handleEmploymentOption = (val: EmploymentOption | undefined, e: any) => {
		const { checked } = e.target;

		let newVal: EmploymentOption | undefined;

		if (employmentOption === val && !checked) {
			newVal = undefined;
		} else if (checked) {
			newVal = val;
		}

		setEmploymentOption(newVal);

		triggerOnChange({ employmentOption: newVal });
	};

	const handleUnion = (text: string) => {
		triggerOnChange({ union: text });
	};

	const handleTitle = (text: string) => {
		triggerOnChange({ title: text });
	};

	const handleEmploymentStart = (date: Date) => {
		setEmploymentStart(date);
		triggerOnChange({ started: date });
	};

	const handleEmploymentEndDate = (date: Date) => {
		setEmploymentEndDate(date);
		triggerOnChange({ ended: date });
	};

	const handleEmploymentFirstStartDate = (date: Date) => {
		setEmploymentFirstStartDate(date);
		triggerOnChange({ firstStarted: date });
	};

	function triggerOnChange(change: Partial<Employment>) {
		const updateEmployment: Employment = {
			union,
			title,
			employmentOption,
			colAgreement: colAgreementId ? { id: colAgreementId } : undefined,
			firstStarted: employmentFirstStartDate,
			started: employmentStart,
			ended: employmentEndDate,
			...change,
		};

		if (isCreate) {
			// If we are creating a new consultant we only set the first started date
			// and then copy it to the "started" date, also we do not allow to set the "ended" date
			delete updateEmployment.ended;
			updateEmployment.started = employmentFirstStartDate;
		}

		onChange && onChange(updateEmployment);
	}

	return (
		<div className="flex flex-col w-full gap-5">
			<div className="flex gap-4 w-full">
				<AppDatePicker
					label="consultant.employment.firstStarted"
					inputStyles={true}
					selectedDate={employmentFirstStartDate}
					onChange={handleEmploymentFirstStartDate}
					margin="0"
					mandatory
				/>
				{!isCreate && (
					<AppDatePicker
						label="consultant.employment.started"
						inputStyles={true}
						selectedDate={employmentStart}
						onChange={handleEmploymentStart}
						margin="0"
						mandatory
					/>
				)}
				{!isCreate && (
					<AppDatePicker
						label="END_DATE"
						inputStyles={true}
						selectedDate={employmentEndDate}
						onChange={handleEmploymentEndDate}
						margin="0"
						tooltip={t("END_DATE_INFO")}
					/>
				)}
			</div>
			{!isCreate &&
				((employmentEndDate && dayjs(employmentEndDate).isBefore(employmentStart, "day")) ||
					dayjs(employmentStart).isBefore(employmentFirstStartDate, "day")) && (
					<AppText margin="-1rem 0 0 0" size="xs" color="danger">
						{t("END_DATE_START_DATE_VALIDATION")}
					</AppText>
				)}
			<CollectiveAgreementDropdown
				value={colAgreementId}
				onChange={handleColAgreement}
				label="consultant.collectiveAgreement"
				placeholder="Välj kollektivavtal"
				margin="0"
				showKnowledgeBaseForTagOnHover="consultant-collective-agreement"
			/>
			<AppInputNew
				value={union}
				onChangeText={handleUnion}
				label="consultant.union"
				showKnowledgeBaseForTagOnHover="consultant-union"
			/>

			<div className="flex gap-4">
				<div className="flex flex-row flex-1 gap-2">
					<AppInputNew
						value={title}
						onChangeText={handleTitle}
						label="consultant.title"
						showKnowledgeBaseForTagOnHover="consultant-title"
					/>
				</div>
			</div>
			<div className="flex flex-col gap-3">
				<Checkbox
					margin="0"
					label="consultant.employmentOptions.pension"
					onChange={(e: any) => handleEmploymentOption("AFTER_PENSION", e)}
					isChecked={employmentOption === "AFTER_PENSION"}
				/>
				<Checkbox
					margin="0"
					label="consultant.employmentOptions.student"
					onChange={(e: any) => handleEmploymentOption("STUDENT", e)}
					isChecked={employmentOption === "STUDENT"}
				/>
				<Checkbox
					margin="0"
					label="consultant.employmentOptions.otherMainEmployment"
					onChange={(e: any) => handleEmploymentOption("OTHER_MAIN_EMPLOYMENT", e)}
					isChecked={employmentOption === "OTHER_MAIN_EMPLOYMENT"}
				/>
			</div>
		</div>
	);
};

export default EmploymentAndSalary;

interface EmploymentAndSalaryControllerProps extends EmploymentAndSalaryProps {
	control: Control<any, any>;
	name: string;
}

export const EmploymentAndSalaryController: React.FC<EmploymentAndSalaryControllerProps> = ({
	control,
	name,
	...rest
}) => {
	return (
		<Controller
			control={control}
			name={name}
			render={({ field: { ref, ...fieldWithoutRef } }) => (
				<EmploymentAndSalary {...fieldWithoutRef} {...rest} />
			)}
		/>
	);
};
